import { useState, useEffect, useRef } from 'react';
import { categories, type Category } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="relative min-h-screen flex flex-col">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 flex items-center justify-between h-20">
          {/* Brand */}
          <a href="/" className="flex items-center gap-3 no-underline flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
              <span className="text-background-50 font-bold text-base font-heading">Y</span>
            </div>
            <span className="font-bold text-foreground-950 text-lg whitespace-nowrap font-sans">
              yoybuy spreadsheet
            </span>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-foreground-700 hover:text-foreground-950 transition-colors font-medium text-sm whitespace-nowrap no-underline">
              <i className="ri-home-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
              Главная
            </a>

            <a href="/shopping-guide" className="text-foreground-700 hover:text-foreground-950 transition-colors font-medium text-sm whitespace-nowrap no-underline">
              <i className="ri-shopping-bag-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
              Руководство по покупкам
            </a>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 text-foreground-700 hover:text-foreground-950 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer bg-transparent border-none"
              >
                <i className="ri-function-line text-base w-4 h-4 flex items-center justify-center"></i>
                Категории
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 w-4 h-4 flex items-center justify-center ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-background-50 rounded-2xl shadow-lg border border-background-200/60 py-3 px-2 w-72 z-50">
                  <div className="max-h-96 overflow-y-auto">
                    {categories.map((cat: Category) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 hover:text-foreground-950 no-underline text-sm whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0"></span>
                        {cat.nameRu}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-700 hover:text-foreground-950 transition-colors font-medium text-sm whitespace-nowrap no-underline">
              Kaspreadsheet
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center gap-2 text-foreground-700 hover:text-foreground-950 transition-colors font-medium text-sm whitespace-nowrap bg-transparent border-none cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/50 px-4 py-4">
            <div className="flex flex-col gap-1">
              <a
                href="/"
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 hover:text-foreground-950 no-underline text-sm font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Главная
              </a>
              <a
                href="/shopping-guide"
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 hover:text-foreground-950 no-underline text-sm font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-shopping-bag-line w-5 h-5 flex items-center justify-center"></i>
                Руководство по покупкам
              </a>
              <div className="px-3 py-2">
                <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider mb-2 font-sans">Категории</p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat: Category) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 hover:text-foreground-950 no-underline text-xs font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                      {cat.nameRu}
                    </a>
                  ))}
                </div>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 hover:text-foreground-950 no-underline text-sm font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-external-link-line w-5 h-5 flex items-center justify-center"></i>
                Kaspreadsheet
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="flex-1 flex flex-col items-center justify-center pt-32 pb-16 px-6 relative">
        {/* Hero Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20minimalist%20abstract%20composition%20with%20warm%20ivory%20sand%20and%20muted%20coral%20tones%2C%20floating%20fluid%20organic%20shapes%20with%20gentle%20curves%2C%20clean%20modern%20Scandinavian%20aesthetic%2C%20diffused%20ethereal%20lighting%2C%20luxurious%20editorial%20design%20background%2C%20subtle%20texture%20gradients%2C%20sophisticated%20neutral%20palette%2C%20no%20text%20no%20people%2C%20elegant%20contemporary%20art&width=1800&height=1000&seq=yoybuy-hero-v2-bg&orientation=landscape"
            alt="YOYBUY Spreadsheet фон"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/5 to-background-50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center">
          {/* Capsule Tag */}
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-foreground-300/60 mb-8">
            <span className="text-foreground-700 text-sm font-medium font-sans whitespace-nowrap">
              YOYBUY Агент 2025
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 text-center leading-tight mb-4">
            Ваш надёжный<br />
            <span className="relative inline-block">
              табличный агент
              <span className="inline-block align-middle mx-1 w-8 h-8 md:w-10 md:h-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20white%20leather%20sneaker%20with%20subtle%20gold%20accent%20detail%20floating%20on%20warm%20cream%20background%2C%20clean%20isolated%20product%20photography%2C%20soft%20natural%20shadow%20beneath%2C%20Scandinavian%20minimalist%20editorial%20style%2C%20warm%20neutral%20tones%2C%20soft%20directional%20lighting%2C%20high%20detail%20leather%20texture%2C%20professional%20luxury%20fashion%20catalog%20shot%2C%20simple%20refined%20composition&width=600&height=600&seq=yoybuy-hero-v2-product-01&orientation=squarish"
                  alt=""
                  className="w-full h-full object-cover rounded-lg"
                />
              </span>
              для заказа
            </span>
            <br />из Китая
          </h1>

          {/* Product Fan - 4 unique cards */}
          <div className="flex items-end justify-center gap-3 md:gap-5 mt-10 mb-10">
            <div className="w-36 h-44 md:w-44 md:h-52 rounded-2xl overflow-hidden bg-background-50 border border-background-200/50 -mr-1 flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Oversized%20chunky%20knit%20oatmeal%20sweater%20floating%20on%20cream%20background%2C%20visible%20cable%20knit%20texture%2C%20minimalist%20product%20photography%2C%20clean%20isolated%20shot%2C%20subtle%20soft%20shadow%20beneath%2C%20Scandinavian%20editorial%20style%2C%20warm%20neutral%20lighting%2C%20professional%20fashion%20catalog%20image%2C%20high%20detail%20wool%20texture%2C%20simple%20refined%20composition&width=600&height=600&seq=yoybuy-hero-v2-product-02&orientation=squarish"
                alt="Свитер"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-36 h-44 md:w-44 md:h-52 rounded-2xl overflow-hidden bg-background-50 border border-background-200/50 md:-mt-8 z-10 flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Luxurious%20dark%20brown%20leather%20backpack%20with%20gold%20zipper%20details%20floating%20on%20cream%20background%2C%20clean%20product%20photography%2C%20isolated%20shot%2C%20subtle%20shadow%2C%20Scandinavian%20editorial%20style%2C%20warm%20neutral%20tones%2C%20soft%20natural%20lighting%2C%20professional%20catalog%20image%2C%20high%20detail%20premium%20leather%20texture%2C%20simple%20elegant%20composition&width=600&height=600&seq=yoybuy-hero-v2-product-03&orientation=squarish"
                alt="Рюкзак"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-36 h-44 md:w-44 md:h-52 rounded-2xl overflow-hidden bg-background-50 border border-background-200/50 z-20 flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20light%20grey%20cotton%20t-shirt%20neatly%20folded%20floating%20on%20cream%20background%2C%20clean%20product%20photography%2C%20isolated%20shot%2C%20subtle%20shadow%2C%20Scandinavian%20editorial%20style%2C%20warm%20neutral%20tones%2C%20soft%20natural%20lighting%2C%20professional%20catalog%20image%2C%20high%20detail%20soft%20fabric%20texture%2C%20simple%20refined%20composition&width=600&height=600&seq=yoybuy-hero-v2-product-04&orientation=squarish"
                alt="Футболка"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="w-36 h-44 md:w-44 md:h-52 rounded-2xl overflow-hidden bg-background-50 border border-background-200/50 -ml-1 flex-shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20woven%20leather%20belt%20with%20polished%20silver%20buckle%20floating%20on%20cream%20background%2C%20clean%20product%20photography%2C%20isolated%20shot%2C%20subtle%20shadow%2C%20Scandinavian%20editorial%20style%2C%20warm%20neutral%20tones%2C%20soft%20natural%20lighting%2C%20professional%20catalog%20image%2C%20high%20detail%20leather%20grain%20texture%2C%20simple%20refined%20composition&width=600&height=600&seq=yoybuy-hero-v2-product-05&orientation=squarish"
                alt="Ремень"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-foreground-600 text-base md:text-lg text-center max-w-xl leading-relaxed mb-8 font-sans">
            YOYBUY Spreadsheet — это современный инструмент для заказа товаров из Китая через проверенных агентов. Экономьте время и деньги с нашей табличной системой.
          </p>

          {/* CTA Buttons */}
          <div className="flex items-center gap-4 md:gap-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-foreground-950 text-background-50 rounded-full font-semibold text-sm hover:bg-foreground-800 transition-colors no-underline whitespace-nowrap cursor-pointer"
            >
              yoybuy sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-foreground-600 hover:text-foreground-950 font-medium text-sm whitespace-nowrap no-underline transition-colors cursor-pointer"
            >
              buy it now
              <i className="ri-arrow-right-line ml-1 w-3 h-3 inline-flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}